/**
 * Base query with automatic token refresh for RTK Query
 * This will automatically refresh tokens when they expire
 * Also includes retry logic for transient server errors (5xx, 429)
 * Also captures failed API requests as audit log entries (system_error)
 */
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout, setCredentials } from '../Authentication/authSlice';
import { getAuthBaseUrl } from './api';

// Track if we're currently refreshing to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise = null;

// ── API Error Logging (audit capture) ──────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const errorThrottleMap = new Map(); // endpoint → last-logged timestamp
const ERROR_THROTTLE_MS = 30000; // 30 seconds per endpoint

const shouldLogError = (url, status) => {
  // Never log audit-log endpoints (prevent recursion)
  if (url?.includes('/audit-logs/')) return false;
  // Skip auth endpoints (handled by token refresh logic)
  if (url?.includes('/auth/')) return false;
  // Skip 401s (handled by token refresh)
  if (status === 401) return false;
  // Throttle: skip if same endpoint errored within 30s
  const now = Date.now();
  const lastLogged = errorThrottleMap.get(url);
  if (lastLogged && now - lastLogged < ERROR_THROTTLE_MS) return false;
  errorThrottleMap.set(url, now);
  return true;
};

/**
 * Log a failed API request to the audit system using fetch() (avoids RTK Query recursion)
 */
const logApiError = (args, error, storeState) => {
  try {
    const url = typeof args === 'string' ? args : args?.url || '';
    const method = typeof args === 'string' ? 'GET' : args?.method || 'GET';
    const status = error?.status || error?.originalStatus || 0;

    if (!shouldLogError(url, status)) return;

    const token = storeState?.auth?.token;
    if (!token) return;

    const portal = storeState?.auth?.user?.assigned_portal || 'unknown';
    const errorBody =
      typeof error?.data === 'string'
        ? error.data.slice(0, 500)
        : JSON.stringify(error?.data || error?.error || 'Unknown error').slice(0, 500);

    fetch(`${API_BASE_URL}/api/v1/rbac/audit-logs/frontend-event/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_type: 'system_error',
        severity: status >= 500 ? 'high' : 'medium',
        description: `API ${method} ${url} failed with status ${status}`,
        module: 'frontend',
        action: 'api_error',
        metadata: {
          endpoint: url,
          method,
          status,
          error_body: errorBody,
          page: window.location.pathname,
          portal,
          user_agent: navigator.userAgent,
        },
      }),
    }).catch(() => {
      // Silently ignore — cannot recurse
    });
  } catch {
    // Silently ignore
  }
};

// Retry configuration for transient server errors
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  multiplier: 2,
  retryableStatuses: [500, 502, 503, 504, 429], // Server errors + rate limiting
  nonRetryableStatuses: [400, 401, 403, 404, 422], // Client errors - don't retry
};

/**
 * Check if an error status is retryable
 */
const isRetryableError = (error) => {
  if (!error) return false;
  const status = error.status || error.originalStatus;
  return RETRY_CONFIG.retryableStatuses.includes(status);
};

/**
 * Calculate backoff delay with jitter to prevent thundering herd
 */
const calculateBackoffDelay = (attempt) => {
  const baseDelay = Math.min(
    RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.multiplier, attempt),
    RETRY_CONFIG.maxDelay
  );
  // Add jitter (25% random variance) to prevent thundering herd
  return baseDelay * (0.75 + Math.random() * 0.5);
};

/**
 * Delay helper function
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const baseQuery = (baseUrl) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const state = getState();
      const token = state.auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }

      // Tell the backend which organisation this session is currently acting in.
      // Only meaningful for users who belong to more than one; the server
      // validates membership and falls back to the primary organisation, so a
      // stale or forged value can never widen access.
      const activeOrgId = state.auth.currentOrganization?.id;
      if (activeOrgId) {
        headers.set('X-Organization-Id', String(activeOrgId));
      }

      // Don't set content-type here - let fetchBaseQuery handle it
      // For FormData, the browser will set the correct multipart/form-data with boundaries
      // For JSON, fetchBaseQuery will set application/json automatically
      return headers;
    },
  });

export const baseQueryWithReauth = (baseUrl) => async (args, api, extraOptions) => {
  const url = typeof args === 'string' ? args : args.url;

  let result = await baseQuery(baseUrl)(args, api, extraOptions);

  // Handle retryable server errors (5xx, 429) with exponential backoff
  // Only retry safe/idempotent methods — POST/PUT/DELETE are NOT retried
  // to prevent duplicate resource creation
  const method = (typeof args === 'string' ? 'GET' : args.method || 'GET').toUpperCase();
  const isSafeMethod = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';

  if (result.error && isSafeMethod && isRetryableError(result.error)) {
    for (let attempt = 1; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
      const backoffDelay = calculateBackoffDelay(attempt - 1);

      await delay(backoffDelay);

      // Retry the request
      result = await baseQuery(baseUrl)(args, api, extraOptions);

      // Success or non-retryable error - break out of retry loop
      if (!result.error || !isRetryableError(result.error) || result.error.status === 401) {
        break;
      }
    }
  }

  // Log failed API requests (after retries exhausted, before token refresh)
  if (result.error && result.error.status !== 401) {
    logApiError(args, result.error, api.getState());
  }

  // If we get a 401, try to refresh the token
  if (result.error && result.error.status === 401) {
    // Don't try to refresh for login or token refresh endpoints
    if (url?.includes('/login/') || url?.includes('/token/')) {
      return result;
    }

    const refreshToken = api.getState().auth.refreshToken;

    if (refreshToken) {
      // If we're already refreshing, wait for it to complete
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          // Retry the original request with the new token
          return baseQuery(baseUrl)(args, api, extraOptions);
        } catch (error) {
          return result;
        }
      }

      // Start refreshing
      isRefreshing = true;

      // Always use RBAC API base URL for token refresh
      const authBaseUrl = getAuthBaseUrl();

      // Create the refresh promise
      refreshPromise = baseQuery(authBaseUrl)(
        {
          url: 'auth/refresh/',
          method: 'POST',
          body: { refresh: refreshToken },
        },
        api,
        extraOptions
      );

      try {
        // Try to get a new token
        const refreshResult = await refreshPromise;

        if (refreshResult.data) {
          // Store the new token
          const { access, refresh } = refreshResult.data;

          api.dispatch(
            setCredentials({
              token: access,
              refreshToken: refresh || refreshToken,
              user: api.getState().auth.user,
              organizations: api.getState().auth.organizations,
              defaultOrganization: api.getState().auth.defaultOrganization,
              permissions: api.getState().auth.permissions,
              roles: api.getState().auth.roles,
              maxRoleLevel: api.getState().auth.maxRoleLevel,
              currentOrganization: api.getState().auth.currentOrganization,
            })
          );

          // Retry the original query with new token
          result = await baseQuery(baseUrl)(args, api, extraOptions);
        } else {
          // Refresh failed, logout
          api.dispatch(logout());
        }
      } catch {
        api.dispatch(logout());
      } finally {
        // Reset the refresh state
        isRefreshing = false;
        refreshPromise = null;
      }
    } else {
      // No refresh token, logout
      api.dispatch(logout());
    }
  }

  return result;
};
